import {
  type Control,
  type UseFormSetValue,
  type UseFormTrigger,
  useWatch,
} from "react-hook-form";
import HomeIcon from "@mui/icons-material/Home";
import { Button } from "@mui/material";
import { FormAccordionSection } from "Components/Shared/FormAccordionSection";
import { AddressInput } from "Components/Shared/Inputs/Form/AddressInput";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { AddressDto, AddressSuggestionDto } from "Infrastructure/Api/Api";
import type { FacilityFormModel } from "Components/Shared/Merchant/Facilities/schema";
import { Resources, useResource } from "Translations/Resources";
import { copyAddressDtoToFormFields } from "Utils/AddressUtils";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;

type Props = {
  control: Control<FacilityFormModel>;
  setValue: UseFormSetValue<FacilityFormModel>;
  trigger: UseFormTrigger<FacilityFormModel>;
  isExpanded: boolean;
  onToggle: () => void;
  onNext: () => void;
  isComplete: boolean | undefined;
  isIncompleteIconHidden: boolean;
  hasErrors: boolean;
  companyHeadquartersAddress?: AddressDto;
};

export const AddressSection = ({
  control,
  setValue,
  trigger,
  isExpanded,
  onToggle,
  onNext,
  isComplete,
  isIncompleteIconHidden,
  hasErrors,
  companyHeadquartersAddress,
}: Props) => {
  const { t } = useResource();
  const address = useWatch({ control, name: "address" });

  const handleAcceptAddressSuggestion = (suggestion: AddressSuggestionDto) => {
    copyAddressDtoToFormFields(suggestion, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  const handleClearAddress = () => {
    copyAddressDtoToFormFields({}, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  return (
    <FormAccordionSection
      legend={t(RESOURCES.Legend.Address)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      onNext={onNext}
      isComplete={isComplete}
      hideIncompleteIcon={isIncompleteIconHidden}
      hasErrors={hasErrors}
    >
      {companyHeadquartersAddress && (
        <Button
          variant="outlined"
          startIcon={<HomeIcon />}
          onClick={() => {
            copyAddressDtoToFormFields(
              companyHeadquartersAddress,
              (field, value) =>
                setValue(`address.${field}`, value, { shouldDirty: true }),
            );
            trigger("address");
          }}
          fullWidth
        >
          {t(RESOURCES.UseRegisteredAddress)}
        </Button>
      )}
      <AddressInput
        address={address}
        onAcceptSuggestion={handleAcceptAddressSuggestion}
        onClear={handleClearAddress}
        hasErrors={hasErrors}
      >
        <FormInput
          control={control}
          name="address.street"
          label={t(RESOURCES.Fields.Address.Street)}
        />
        <FormInput
          control={control}
          name="address.streetNumber"
          label={t(RESOURCES.Fields.Address.StreetNumber)}
        />
        <FormInput
          control={control}
          name="address.orientationNumber"
          label={t(RESOURCES.Fields.Address.OrientationNumber)}
        />
        <FormInput
          control={control}
          name="address.postalCode"
          label={t(RESOURCES.Fields.Address.PostalCode)}
        />
        <FormInput
          control={control}
          name="address.municipality"
          label={t(RESOURCES.Fields.Address.Municipality)}
        />
      </AddressInput>
    </FormAccordionSection>
  );
};
